// src/pages/admin/ManageUsersPage.js
// Updated: 11/10/2025 06:17 PM EST
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useEffect } from 'react';
// --- 1. IMPORT 'functions' and 'httpsCallable' ---
import { db, functions } from '../../firebaseConfig'; 
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
// --- 2. IMPORT new icons ---
import { Settings, User, Users, Eye, Edit, UserX, UserCheck } from 'lucide-react';

// --- 3. DEFINE THE CALLABLE FUNCTIONS ---
const adminChangeUserRole = httpsCallable(functions, 'adminChangeUserRole');
const adminSetUserStatus = httpsCallable(functions, 'adminSetUserStatus');

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

// Map internal role strings to display icons/colors
const RoleOptions = [
    { value: 'admin', label: 'Admin', icon: Settings, color: 'text-purple-600' },
    { value: 'member', label: 'Member', icon: Users, color: 'text-blue-600' },
    { value: 'reviewer', label: 'Reviewer', icon: Edit, color: 'text-green-600' },
    { value: 'viewer', label: 'Viewer/Guest', icon: Eye, color: 'text-gray-500' },
];


export default function ManageUsersPage() {
    const { userData, currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    // Use an object to track multiple updates concurrently
    const [isUpdatingStatus, setIsUpdatingStatus] = useState({});
    const [isUpdatingRole, setIsUpdatingRole] = useState({});

    // Fetch and listen for real-time updates to users in the organization (No change)
    useEffect(() => {
        if (!userData?.orgId) {
            setError("Organization ID is missing.");
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        const usersQuery = query(
            collection(db, 'users'),
            where('orgId', '==', userData.orgId)
        );

        const unsubscribe = onSnapshot(usersQuery, (querySnapshot) => {
            const usersData = [];
            querySnapshot.forEach((doc) => {
                // Ensure default status is 'active' for existing users without the field
                usersData.push({ id: doc.id, status: 'active', ...doc.data() });
            });
            setUsers(usersData);
            setIsLoading(false);
            setError('');
        }, (err) => {
            console.error('Error fetching users:', err);
            setError('Failed to load user list.');
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userData?.orgId]);

    // Handler to change user role (Minor update to state tracking)
    const handleRoleChange = async (userId, newRole) => {
        if (!window.confirm(`Are you sure you want to change this user's role to ${newRole.toUpperCase()}?`)) {
            return;
        }
        
        setIsUpdatingRole(prev => ({...prev, [userId]: true}));
        setError('');
        setStatusMessage(`Updating role for ${users.find(u => u.id === userId)?.displayName}...`);

        try {
            await adminChangeUserRole({
              targetUserId: userId,
              newRole: newRole,
              orgId: userData.orgId
            });
            
            setStatusMessage(`Successfully updated role to ${newRole.toUpperCase()}.`);
        } catch (err) {
            console.error("Error updating user role:", err);
            setError(`Failed to update role: ${err.message}`);
            setStatusMessage('');
        }
        
        setIsUpdatingRole(prev => ({...prev, [userId]: false}));
        setTimeout(() => {
          setStatusMessage('');
          setError('');
        }, 5000);
    };

    // --- 4. NEW: Handler to set user status (Inactivate/Reactivate) ---
    const handleSetUserStatus = async (user) => {
        const isCurrentlyActive = user.status === 'active';
        const newStatus = isCurrentlyActive ? 'inactive' : 'active';
        const actionText = isCurrentlyActive ? 'inactivate' : 'reactivate';

        if (!window.confirm(`Are you sure you want to ${actionText} ${user.displayName}? This will prevent/allow them from logging into the app.`)) {
            return;
        }

        setIsUpdatingStatus(prev => ({...prev, [user.id]: true}));
        setError('');
        setStatusMessage(`Setting status to ${newStatus.toUpperCase()} for ${user.displayName}...`);

        try {
            await adminSetUserStatus({
              targetUserId: user.id,
              newStatus: newStatus,
              orgId: userData.orgId
            });
            
            setStatusMessage(`Successfully ${newStatus}d ${user.displayName}.`);
            // Note: AuthContext.js handles immediate logout if status is inactive
        } catch (err) {
            console.error(`Error ${actionText}ing user:`, err);
            setError(`Failed to ${actionText} user: ${err.message}`);
            setStatusMessage('');
        }

        setIsUpdatingStatus(prev => ({...prev, [user.id]: false}));
        setTimeout(() => {
            setStatusMessage('');
            setError('');
        }, 5000);
    };
    // --- END NEW HANDLER ---

    if (isLoading) return <LoadingSpinner />;
    if (error) return <p className="text-red-600">{error}</p>;

    return (
        <div className="rounded-lg bg-white p-6 shadow-lg">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">Manage Organization Users ({users.length})</h1>

            {statusMessage && (
                <div className="mb-4 rounded-md bg-blue-50 p-3 text-sm text-blue-700">
                    {statusMessage}
                </div>
            )}
            
            <p className="text-sm text-gray-500 mb-4">
                Admins can change roles and inactivate users to temporarily suspend their access. Inactivated users cannot log in.
            </p>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name & Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Current Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th> {/* 5. ADD Status Column */}
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {users.map((user) => {
                            const isCurrentUser = user.id === currentUser.uid;
                            const currentRole = RoleOptions.find(r => r.value === user.role);
                            const isActive = user.status === 'active';
                            const isUserUpdating = isUpdatingRole[user.id] || isUpdatingStatus[user.id];

                            return (
                                <tr key={user.id} className={isCurrentUser ? 'bg-yellow-50' : (isActive ? '' : 'bg-red-50')}>
                                    
                                    {/* Name Cell */}
                                    <td className="whitespace-nowrap px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">{user.displayName} {isCurrentUser && <span className="ml-1 text-xs text-indigo-600">(You)</span>}</div>
                                        <div className="text-xs text-gray-500">{user.email}</div>
                                    </td>
                                    
                                    {/* Role Cell */}
                                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                                        <span className={`inline-flex items-center rounded-full px-3 py-0.5 text-xs font-medium ${currentRole?.color || 'text-gray-500'}`}>
                                            {currentRole ? <currentRole.icon className="mr-1 h-3 w-3" /> : <User className="mr-1 h-3 w-3" />}
                                            {currentRole?.label || user.role}
                                        </span>
                                    </td>

                                    {/* 6. Status Cell */}
                                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                                        {isActive ? (
                                            <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-0.5 text-xs font-medium text-green-800">Active</span>
                                        ) : (
                                            <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-0.5 text-xs font-medium text-red-800">Inactive</span>
                                        )}
                                    </td>

                                    {/* Actions Cell (Role Select) */}
                                    <td className="whitespace-nowrap px-6 py-4 text-sm space-x-2">
                                        <select
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            disabled={isCurrentUser || isUserUpdating}
                                            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-70"
                                        >
                                            {RoleOptions.map(role => (
                                                <option key={role.value} value={role.value}>
                                                    {role.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    
                                    {/* Actions Cell (Status Button) */}
                                    <td className="whitespace-nowrap px-6 py-4 text-sm space-x-2">
                                        {isCurrentUser ? (
                                            <span className="text-gray-400 text-xs">Cannot modify self</span>
                                        ) : (
                                          <button
                                            onClick={() => handleSetUserStatus(user)}
                                            disabled={isUserUpdating}
                                            className={`flex items-center rounded px-3 py-1 text-xs font-medium shadow-sm disabled:opacity-50 ${
                                                isActive
                                                    ? 'bg-red-50 text-red-700 hover:bg-red-100'
                                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                                            }`}
                                          >
                                            {isUserUpdating ? <LoadingSpinner /> : (
                                                isActive ? <UserX className="mr-1 h-3 w-3" /> : <UserCheck className="mr-1 h-3 w-3" />
                                            )}
                                            {isActive ? 'Inactivate' : 'Reactivate'}
                                          </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}